import React, { FC, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Tooltip,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {
  useTextReplacementsQuery,
  useCreateTextReplacementMutation,
  useUpdateTextReplacementMutation,
  useDeleteTextReplacementMutation,
} from '@/features/internal/text-replacements/hooks/useTextReplacementsQuery'
import type { TextReplacementRule } from '@/features/internal/text-replacements/apis/textReplacements'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { PortalPageHeader } from '@/apps/legacy/components/portal/PortalPageHeader'
import { ActionButton } from '@/apps/legacy/components/portal/CustomButton'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'

type DialogMode = 'create' | 'edit' | 'delete' | null

export const TextReplacementsPage: FC = () => {
  const { rules, isLoading, error } = useTextReplacementsQuery()
  const createMutation = useCreateTextReplacementMutation()
  const updateMutation = useUpdateTextReplacementMutation()
  const deleteMutation = useDeleteTextReplacementMutation()

  const [dialogMode, setDialogMode] = useState<DialogMode>(null)
  const [selectedRule, setSelectedRule] = useState<TextReplacementRule | null>(
    null
  )
  const [formData, setFormData] = useState({
    target_text: '',
    replacement_text: '',
    description: '',
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleOpenCreateDialog = () => {
    setFormData({
      target_text: '',
      replacement_text: '',
      description: '',
    })
    setFormErrors({})
    setDialogMode('create')
  }

  const handleOpenEditDialog = (rule: TextReplacementRule) => {
    setSelectedRule(rule)
    setFormData({
      target_text: rule.target_text,
      replacement_text: rule.replacement_text,
      description: rule.description || '',
    })
    setFormErrors({})
    setDialogMode('edit')
  }

  const handleOpenDeleteDialog = (rule: TextReplacementRule) => {
    setSelectedRule(rule)
    setDialogMode('delete')
  }

  const handleCloseDialog = () => {
    if (!isSubmitting) {
      setDialogMode(null)
      setSelectedRule(null)
      setFormData({
        target_text: '',
        replacement_text: '',
        description: '',
      })
      setFormErrors({})
    }
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!formData.target_text.trim()) {
      errors.target_text = '置換対象文字列は必須です'
    } else if (formData.target_text.length > 200) {
      errors.target_text = '置換対象文字列は200文字以内で入力してください'
    }

    // 空文字は許可（文字列削除用途）
    if (formData.replacement_text.length > 200) {
      errors.replacement_text = '置換後文字列は200文字以内で入力してください'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      if (dialogMode === 'create') {
        await createMutation.mutateAsync({
          target_text: formData.target_text.trim(),
          replacement_text: formData.replacement_text,
          description: formData.description.trim() || undefined,
        })
      } else if (dialogMode === 'edit' && selectedRule) {
        await updateMutation.mutateAsync({
          id: selectedRule.id,
          data: {
            target_text: formData.target_text.trim(),
            replacement_text: formData.replacement_text,
            description: formData.description.trim() || undefined,
          },
        })
      }
      handleCloseDialog()
    } catch (_err) {
      // エラーは mutation 内で処理される
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedRule) return

    setIsSubmitting(true)
    try {
      await deleteMutation.mutateAsync(selectedRule.id)
      handleCloseDialog()
    } catch (_err) {
      // エラーは mutation 内で処理される
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (error && !rules.length) {
    return (
      <PortalLayout>
        <Typography color="error">{error.message}</Typography>
      </PortalLayout>
    )
  }

  return (
    <PortalLayout>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
        }}
      >
        <Box>
          <PortalPageHeader title="文字列置換設定" />
          <Typography
            variant="body2"
            sx={{ color: colorTokens.text.secondary, mt: 1 }}
          >
            SOAP生成時に自動的に適用される文字列置換ルールを管理します。新しいルールほど優先度が高くなります。
          </Typography>
        </Box>
        <ActionButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          新規ルール追加
        </ActionButton>
      </Box>

      <Paper
        elevation={0}
        sx={{
          backgroundColor: colorTokens.background.paper,
          borderRadius: 2,
          border: `1px solid ${colorTokens.border.light}`,
          boxShadow: colorTokens.shadow.card,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${colorTokens.border.light}`,
          }}
        >
          <Typography variant="h6" sx={{ color: colorTokens.text.primary }}>
            置換ルール一覧
          </Typography>
        </Box>

        {isLoading && !rules.length ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <LoadingIndicator />
          </Box>
        ) : rules.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 300,
              p: 4,
            }}
          >
            <SwapHorizIcon
              sx={{
                fontSize: 64,
                color: colorTokens.grey[400],
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: colorTokens.text.secondary,
                mb: 1,
              }}
            >
              置換ルールがありません
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colorTokens.text.secondary,
                textAlign: 'center',
                mb: 3,
              }}
            >
              新規ルール追加ボタンから置換ルールを作成してください
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colorTokens.grey[50] }}>
                  <TableCell
                    sx={{
                      color: colorTokens.text.primary,
                    }}
                  >
                    置換ルール
                  </TableCell>
                  <TableCell
                    sx={{
                      color: colorTokens.text.primary,
                    }}
                  >
                    説明
                  </TableCell>
                  <TableCell
                    sx={{
                      color: colorTokens.text.primary,
                    }}
                  >
                    作成日時
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: colorTokens.text.primary,
                      width: 120,
                    }}
                  >
                    操作
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow
                    key={rule.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: colorTokens.grey[50],
                      },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography>{rule.target_text}</Typography>
                        <ArrowForwardIcon
                          sx={{
                            color: colorTokens.grey[500],
                          }}
                        />
                        <Typography>{rule.replacement_text}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ color: colorTokens.text.secondary }}
                      >
                        {rule.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ color: colorTokens.text.secondary }}
                      >
                        {formatDate(rule.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="編集">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditDialog(rule)}
                          sx={{
                            color: colorTokens.blue[600],
                            '&:hover': {
                              backgroundColor:
                                colorTokens.action.primaryBgLight,
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="削除">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteDialog(rule)}
                          sx={{
                            color: colorTokens.grey[600],
                            '&:hover': {
                              backgroundColor: colorTokens.grey[100],
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* 作成・編集モーダル - カスタム実装 */}
      {(dialogMode === 'create' || dialogMode === 'edit') && (
        <>
          {/* オーバーレイ背景 */}
          <Box
            onClick={handleCloseDialog}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
          {/* モーダル本体 */}
          <Paper
            elevation={24}
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: 600,
              maxHeight: '90vh',
              overflow: 'auto',
              zIndex: 1301,
              borderRadius: 2,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: `1px solid ${colorTokens.border.light}`,
              }}
            >
              <Typography variant="h6">
                {dialogMode === 'create' ? '新規ルール追加' : 'ルール編集'}
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="置換対象文字列"
                  value={formData.target_text}
                  onChange={(e) =>
                    setFormData({ ...formData, target_text: e.target.value })
                  }
                  error={!!formErrors.target_text}
                  helperText={
                    formErrors.target_text ||
                    `入力例: 座薬、ｍｇ（${formData.target_text.length}/200文字）`
                  }
                  fullWidth
                  required
                  disabled={isSubmitting}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="置換後文字列"
                  value={formData.replacement_text}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      replacement_text: e.target.value,
                    })
                  }
                  error={!!formErrors.replacement_text}
                  helperText={
                    formErrors.replacement_text ||
                    `入力例: 坐薬、mg、空欄で削除（${formData.replacement_text.length}/200文字）`
                  }
                  fullWidth
                  disabled={isSubmitting}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="説明"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  helperText="任意: ルールの目的や理由を記載"
                  fullWidth
                  multiline
                  rows={2}
                  disabled={isSubmitting}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                p: 3,
                pt: 2,
                borderTop: `1px solid ${colorTokens.border.light}`,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              <Button
                onClick={handleCloseDialog}
                disabled={isSubmitting}
                sx={{
                  color: colorTokens.text.secondary,
                }}
              >
                キャンセル
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  backgroundColor: colorTokens.blue[600],
                  color: colorTokens.background.paper,
                  minWidth: 100,
                  '&:hover': {
                    backgroundColor: colorTokens.blue[700],
                  },
                }}
              >
                {isSubmitting ? (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      color: colorTokens.background.paper,
                    }}
                  >
                    <LoadingIndicator message="" size={20} />
                  </Box>
                ) : dialogMode === 'create' ? (
                  '追加'
                ) : (
                  '更新'
                )}
              </Button>
            </Box>
          </Paper>
        </>
      )}

      {/* 削除確認モーダル - カスタム実装 */}
      {dialogMode === 'delete' && (
        <>
          {/* オーバーレイ背景 */}
          <Box
            onClick={handleCloseDialog}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
          {/* モーダル本体 */}
          <Paper
            elevation={24}
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: 500,
              maxHeight: '80vh',
              overflow: 'auto',
              zIndex: 1301,
              borderRadius: 2,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: `1px solid ${colorTokens.border.light}`,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: colorTokens.grey[700],
                }}
              >
                ルールを削除
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Typography sx={{ mb: 2 }}>
                以下のルールを削除してもよろしいですか？
              </Typography>
              {selectedRule && (
                <Box
                  sx={{
                    backgroundColor: colorTokens.grey[50],
                    p: 2,
                    borderRadius: 1,
                    border: `1px solid ${colorTokens.border.light}`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography>{selectedRule.target_text}</Typography>
                    <ArrowForwardIcon sx={{ color: colorTokens.grey[600] }} />
                    <Typography>{selectedRule.replacement_text}</Typography>
                  </Box>
                  {selectedRule.description && (
                    <Typography
                      variant="body2"
                      sx={{ color: colorTokens.text.secondary }}
                    >
                      説明: {selectedRule.description}
                    </Typography>
                  )}
                </Box>
              )}
              <Typography
                variant="body2"
                sx={{ color: colorTokens.grey[600], mt: 2 }}
              >
                ※ この操作は取り消せません
              </Typography>
            </Box>
            <Box
              sx={{
                p: 3,
                pt: 2,
                borderTop: `1px solid ${colorTokens.border.light}`,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              <Button
                onClick={handleCloseDialog}
                disabled={isSubmitting}
                sx={{
                  color: colorTokens.text.secondary,
                }}
              >
                キャンセル
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  backgroundColor: colorTokens.grey[600],
                  color: colorTokens.background.paper,
                  minWidth: 100,
                  '&:hover': {
                    backgroundColor: colorTokens.grey[700],
                  },
                }}
              >
                {isSubmitting ? (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      color: colorTokens.background.paper,
                    }}
                  >
                    <LoadingIndicator message="" size={20} />
                  </Box>
                ) : (
                  '削除'
                )}
              </Button>
            </Box>
          </Paper>
        </>
      )}
    </PortalLayout>
  )
}
